import * as React from "react";
import { Box } from "@mui/material";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { useDispatch, useSelector } from "react-redux";
import { ReducersModal } from "utils/modals";
import { getCarTypesListing } from "modules/utilityManagement/action";

interface Props {
  labelProp: string;
  disabled?: boolean;

  /**
   * Can be:
   * - car type ID
   * - car type name
   *
   * We use the name for EDIT because API currently
   * returns carTypeName but carTypeId is empty.
   */
  selectedCarType?: string;

  onSelectCarType: (carType: any) => void;
}

export default function CarTypeDropDown({
  labelProp,
  disabled = false,
  selectedCarType = "",
  onSelectCarType,
}: Props) {
  const dispatch: any = useDispatch();

  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");

  const {
    result = [],
  } = useSelector(
    (store: ReducersModal) =>
      store.CarsTypeReducer
  );

  const options = result || [];

  const fetchCarTypeData = React.useCallback(() => {
    dispatch(
      getCarTypesListing({
        search,
      })
    );
  }, [dispatch, search]);

  React.useEffect(() => {
    fetchCarTypeData();
  }, [fetchCarTypeData]);

  /**
   * Find selected option using either:
   * ID or Name.
   */
  const selectedOption =
    options.find(
      (option: any) =>
        option?.id === selectedCarType ||
        option?._id === selectedCarType ||
        option?.carType === selectedCarType ||
        option?.name === selectedCarType
    ) || null;

  return (
    <Autocomplete
      fullWidth
      disabled={disabled}
      open={open}
      onOpen={() => {
        setOpen(true);
        fetchCarTypeData();
      }}
      onClose={() => {
        setOpen(false);
      }}
      value={selectedOption}
      options={options}
      isOptionEqualToValue={(
        option,
        value
      ) =>
        (option?.id || option?._id) ===
        (value?.id || value?._id)
      }
      getOptionLabel={(option: any) =>
        option?.carType ||
        option?.name ||
        ""
      }
      onChange={(_, value) => {
        onSelectCarType(value);
      }}
      inputValue={search}
      onInputChange={(_, value) => {
        setSearch(value);
      }}
      renderOption={(props, option) => {
        const { key, ...optionProps } =
          props;

        return (
          <Box
            key={key}
            component="li"
            {...optionProps}
          >
            {option?.carType ||
              option?.name}
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label={labelProp}
        />
      )}
    />
  );
}